import inkShell from '../../docs/ink-export/ink-export-shell.ink?raw';
import { narrativeStructure } from '../data/narrativeStructure';
import { NarrativeState, SegmentId } from '../types/narrative';
import { PlanningState } from '../types/planning';

export interface ExportData {
  version: string;
  exportDate: string;
  planning: PlanningState;
  narrative: {
    variables: NarrativeState['variables'];
    nodes: Record<
      string,
      {
        id: string;
        text?: string;
        choices?: Array<{
          id: string;
          text: string;
          npcResponse?: string;
        }>;
        conditionalTextBlocks?: NarrativeState['nodes'][string]['conditionalTextBlocks'];
        textMainGoodWorld?: string;
        textMainBadWorld?: string;
        textBeforeGoodWorld?: string;
        textAfterGoodWorld?: string;
        textBeforeBadWorld?: string;
        textAfterBadWorld?: string;
        choicesGoodWorld?: NarrativeState['nodes'][string]['choicesGoodWorld'];
        choicesBadWorld?: NarrativeState['nodes'][string]['choicesBadWorld'];
        variantContent?: NarrativeState['nodes'][string]['variantContent'];
      }
    >;
    currentNodeId: string;
    currentSegmentId: NarrativeState['currentSegmentId'];
  };
}

export function exportToJson(
  narrativeState: NarrativeState,
  planningState: PlanningState
): ExportData {
  // Extract only the user-entered content from nodes
  const exportNodes: ExportData['narrative']['nodes'] = {};

  Object.entries(narrativeState.nodes).forEach(([nodeId, node]) => {
    const exportNode: ExportData['narrative']['nodes'][string] = {
      id: node.id,
    };

    // Only include fields that have user content
    if (node.text) {
      exportNode.text = node.text;
    }

    if (node.choices) {
      exportNode.choices = node.choices.map((choice) => ({
        id: choice.id,
        text: choice.text,
        npcResponse: choice.npcResponse,
      }));
    }

    if (node.conditionalTextBlocks) {
      exportNode.conditionalTextBlocks = node.conditionalTextBlocks;
    }

    if (node.textMainGoodWorld) {
      exportNode.textMainGoodWorld = node.textMainGoodWorld;
    }

    if (node.textMainBadWorld) {
      exportNode.textMainBadWorld = node.textMainBadWorld;
    }

    if (node.textBeforeGoodWorld) {
      exportNode.textBeforeGoodWorld = node.textBeforeGoodWorld;
    }

    if (node.textAfterGoodWorld) {
      exportNode.textAfterGoodWorld = node.textAfterGoodWorld;
    }

    if (node.textBeforeBadWorld) {
      exportNode.textBeforeBadWorld = node.textBeforeBadWorld;
    }

    if (node.textAfterBadWorld) {
      exportNode.textAfterBadWorld = node.textAfterBadWorld;
    }

    if (node.choicesGoodWorld) {
      exportNode.choicesGoodWorld = node.choicesGoodWorld;
    }

    if (node.choicesBadWorld) {
      exportNode.choicesBadWorld = node.choicesBadWorld;
    }

    if (node.variantContent) {
      exportNode.variantContent = node.variantContent;
    }

    // Only include nodes that have some content
    if (Object.keys(exportNode).length > 1) {
      exportNodes[nodeId] = exportNode;
    }
  });

  return {
    version: '1.0.0',
    exportDate: new Date().toISOString(),
    planning: planningState,
    narrative: {
      variables: narrativeState.variables,
      nodes: exportNodes,
      currentNodeId: narrativeState.currentNodeId,
      currentSegmentId: narrativeState.currentSegmentId,
    },
  };
}

interface MarkdownExportOptions {
  userEmail: string;
  projectName: string;
  includeBPlotSummary: boolean;
  segmentProgress: {
    totalComplete: number;
    totalNodes: number;
    segments: Record<SegmentId, { complete: number; total: number }>;
  };
}

function getReasonLabel(reason: string, other: string): string {
  const map: Record<string, string> = {
    intimidate: 'intimidate',
    intrigue: 'intrigue',
    evade: 'evade',
    show_value: 'show value',
    be_understood: 'be understood',
    find_common_ground: 'find common ground',
  };
  if (!reason) return '[carry out their motivation]';
  return reason === 'other' ? other || '[carry out their motivation]' : map[reason] || reason;
}

function getNatureLabel(nature: string, other: string): string {
  const map: Record<string, string> = {
    anecdote: 'an anecdote',
    philosophical_point: 'a philosophical point',
    parable: 'a parable',
    memory: 'a memory',
    personal_revelation: 'a personal revelation',
    option: 'an option',
  };
  if (!nature) return '[type of diversion]';
  return nature === 'other' ? other || '[type of diversion]' : map[nature] || nature;
}

function buildSceneConcept(planning: PlanningState, includeBPlot: boolean): string {
  const dynamicLabel = planning.dynamicType
    ? {
        familial: 'Familial',
        romantic: 'Romantic',
        transactional: 'Transactional',
        'co-working': 'Co-Working',
      }[planning.dynamicType] || planning.dynamicType
    : '[RELATIONSHIP DYNAMIC]';

  const playerRole = planning.playerRole || '[PLAYER ROLE]';
  const playerName = planning.playerName || '[PLAYER NAME]';
  const playerMotivation = planning.playerMotivation || "[PLAYER'S INITIAL MOTIVATION]";
  const npcRole = planning.npcRole || '[NPC ROLE]';
  const npcName = planning.npcName || '[NPC NAME]';
  const npcMotivation = planning.npcMotivation || "[NPC'S CORE MOTIVATION]";
  const aPlot = planning.aPlot || '[THE A-PLOT]';
  const stakes = planning.stakes || '[STAKES]';
  const location = planning.locationAtmosphere || '[LOCATION]';

  let concept = `The ${playerRole} named ${playerName}, driven by ${playerMotivation}, and the ${npcRole} named ${npcName}, motivated by ${npcMotivation}, are talking about ${aPlot}.`;
  concept += ` The game starts mid-way through a conversation between these two people while they are in ${location}. Their relationship dynamic is one of a '${dynamicLabel}' situation, with stakes ultimately involving ${stakes}.`;

  if (includeBPlot) {
    const natureText = getNatureLabel(planning.bPlotNature, planning.bPlotNatureOther);
    const reasonText = getReasonLabel(planning.bPlotReason, planning.bPlotReasonOther);
    const explanation = planning.bPlotExplanation || '[EXPLANATION]';
    concept += `\n${npcName || '[NPC NAME]'} diverts the conversation via ${natureText} in order to ${reasonText} -- ${natureText} being their chosen form of diversion because of ${explanation}.`;
  }

  return concept;
}

export function exportToMarkdown(exportData: ExportData, options: MarkdownExportOptions): string {
  const { planning, narrative } = exportData;
  const { userEmail, projectName, includeBPlotSummary, segmentProgress } = options;

  // Determine which segments have been started (have at least one completed node)
  const startedSegments = Object.entries(segmentProgress.segments)
    .filter(([, progress]) => progress.complete > 0)
    .map(([segmentId]) => segmentId as SegmentId);

  let markdown = '';
  markdown += `USER: ${userEmail}\n`;
  markdown += `PROJECT NAME: ${projectName}\n`;
  markdown += 'CREATED FOR: WIA: GAME LAB 101 SCENE WRITING\n';
  markdown += `EXPORTED: ${new Date(exportData.exportDate).toLocaleString()}\n\n`;

  // Only show completion stats for started segments
  const startedSegmentProgress = startedSegments.reduce(
    (acc, segmentId) => {
      acc.complete += segmentProgress.segments[segmentId].complete;
      acc.total += segmentProgress.segments[segmentId].total;
      return acc;
    },
    { complete: 0, total: 0 }
  );

  markdown += `PROJECT COMPLETION (TOTAL: ${startedSegmentProgress.complete}/${startedSegmentProgress.total})\n`;

  // Only include progress lines for started segments
  startedSegments.forEach((segmentId) => {
    const progress = segmentProgress.segments[segmentId];
    const segmentLabel =
      segmentId === '3A'
        ? 'SEGMENT 3A'
        : segmentId === '3B'
          ? 'SEGMENT 3B'
          : `SEGMENT ${segmentId}`;
    markdown += `${segmentLabel}: ${progress.complete}/${progress.total}\n`;
  });
  markdown += '\n';

  markdown += 'SCENE CONCEPT:\n';
  markdown += ` '${buildSceneConcept(planning, includeBPlotSummary)}'\n\n`;

  // Only include nodes from started segments
  const startedSegmentSet = new Set(startedSegments);
  const filteredNodes = Object.entries(narrative.nodes).filter(([nodeId]) => {
    // Get the segment ID from the narrative structure
    const structureNode = narrativeStructure[nodeId];
    if (!structureNode) return false;

    const nodeSegmentId = structureNode.segmentId.toString();
    return startedSegmentSet.has(nodeSegmentId as SegmentId);
  });

  if (filteredNodes.length === 0) {
    markdown += '### No Content Available\n\n';
    markdown +=
      'No segments have been started yet. Complete at least the first node of a segment to see it in the export.\n\n';
    return markdown;
  }

  // Nodes
  markdown += '### Nodes\n\n';
  filteredNodes.forEach(([nodeId, node]) => {
    const structure = narrativeStructure[nodeId];
    markdown += `#### ${nodeId}\n`;

    if (structure?.idAlias) {
      markdown += `// ${structure.idAlias}\n`;
    }
    if (structure?.label) {
      markdown += `// ${structure.label}\n`;
    }
    if (structure?.guidance) {
      markdown += structure.guidance
        .split('\n')
        .map((line) => `// ${line}`)
        .join('\n');
      markdown += '\n';
    }
    if (structure?.type) {
      markdown += `// type: ${structure.type}\n`;
    }
    markdown += '\n';

    if (node.text) {
      markdown += '// Text\n';
      markdown += `${node.text}\n\n`;
    }

    if (node.choices && node.choices.length > 0) {
      markdown += '// Choices\n';
      node.choices.forEach((choice, index) => {
        const structChoice = structure?.choices?.find((c) => c.id === choice.id);
        const goto = structChoice?.goto ? ` -> ${structChoice.goto}` : '';
        markdown += `${index + 1}. ${choice.text}${goto}\n`;
        if (choice.npcResponse) {
          markdown += `// NPC Response\n${choice.npcResponse}\n`;
        }
      });
      markdown += '\n';
    }

    if (node.conditionalTextBlocks) {
      markdown += '// Conditional Text Blocks\n';
      if (node.conditionalTextBlocks.goodWorld.length > 0) {
        markdown += '- Good World:\n';
        node.conditionalTextBlocks.goodWorld.forEach((block) => {
          markdown += `  - ${block.condition}: ${block.textRecall}\n`;
        });
      }
      if (node.conditionalTextBlocks.badWorld.length > 0) {
        markdown += '- Bad World:\n';
        node.conditionalTextBlocks.badWorld.forEach((block) => {
          markdown += `  - ${block.condition}: ${block.textRecall}\n`;
        });
      }
      markdown += '\n';
    }

    if (node.textBeforeGoodWorld || node.textAfterGoodWorld || node.textMainGoodWorld) {
      const goodText =
        node.textBeforeGoodWorld || node.textAfterGoodWorld
          ? `${node.textBeforeGoodWorld || ''}[VARIANT]${node.textAfterGoodWorld || ''}`
          : node.textMainGoodWorld || '';
      if (goodText.trim()) {
        markdown += '// Good World Text\n';
        markdown += `${goodText}\n\n`;
      }
    }

    if (node.textBeforeBadWorld || node.textAfterBadWorld || node.textMainBadWorld) {
      const badText =
        node.textBeforeBadWorld || node.textAfterBadWorld
          ? `${node.textBeforeBadWorld || ''}[VARIANT]${node.textAfterBadWorld || ''}`
          : node.textMainBadWorld || '';
      if (badText.trim()) {
        markdown += '// Bad World Text\n';
        markdown += `${badText}\n\n`;
      }
    }

    if (node.variantContent) {
      // Show good world variants
      if (
        node.variantContent.goodWorld?.variants &&
        node.variantContent.goodWorld.variants.length > 0
      ) {
        markdown += '**Variant Content (Better World):**\n';
        node.variantContent.goodWorld.variants.forEach((variant) => {
          markdown += `- Choice "${variant.choiceText}": ${variant.variantLine}\n`;
        });
        markdown += '\n';
      }
      // Show bad world variants
      if (
        node.variantContent.badWorld?.variants &&
        node.variantContent.badWorld.variants.length > 0
      ) {
        markdown += '**Variant Content (Worse World):**\n';
        node.variantContent.badWorld.variants.forEach((variant) => {
          markdown += `- Choice "${variant.choiceText}": ${variant.variantLine}\n`;
        });
        markdown += '\n';
      }
    }
  });

  return markdown;
}

export function exportToInkSource(exportData: ExportData): string {
  const escapeInk = (text: string): string =>
    text
      .replace(/\\/g, '\\\\')
      .replace(/\{/g, '\\{')
      .replace(/\}/g, '\\}')
      .replace(/\[/g, '\\[')
      .replace(/\]/g, '\\]')
      .replace(/"/g, '\\"');

  let ink = inkShell;
  const nodes = exportData.narrative.nodes;

  // Replace main node text
  ink = ink.replace(/\{\{NODE_TEXT\.([\w-]+)\}\}/g, (_m, nodeId) => {
    const text = nodes[nodeId]?.text || '';
    return escapeInk(text);
  });

  // Replace choice text
  ink = ink.replace(/\{\{CHOICE_TEXT\.([\w-]+)\.(\d+)\}\}/g, (_m, nodeId, idx) => {
    const text = nodes[nodeId]?.choices?.[Number(idx)]?.text || '';
    return escapeInk(text);
  });

  // Replace NPC response blocks
  ink = ink.replace(/\{\{NPC_RESPONSE_BLOCK\.([\w-]+)\.(\d+)\}\}/g, (_m, nodeId, idx) => {
    const resp = nodes[nodeId]?.choices?.[Number(idx)]?.npcResponse;
    return resp ? `NPC Response: ${escapeInk(resp)}` : '';
  });

  // Replace variable assignment blocks
  ink = ink.replace(/\{\{VAR_ASSIGN_BLOCK\.([\w-]+)\.(\d+)\}\}/g, (_m, nodeId, idx) => {
    if (nodeId === 'S1_Player_Declaration') {
      const choiceId = nodes[nodeId]?.choices?.[Number(idx)]?.id;
      return choiceId ? `~ player_node2_statement_for_npc_memory = "${choiceId}"` : '';
    }
    return '';
  });

  // Variant lines for memory recall (updated for new structure)
  ink = ink.replace(
    /\{\{VARIANT_LINE\.(S3A_NPC_Recall_Positive|S3B_NPC_Recall_Negative)\.(n2mc-c\d+)\}\}/g,
    (_m, nodeId, choiceId) => {
      const worldKey = nodeId.startsWith('S3A_') ? 'goodWorld' : 'badWorld';
      const variant = nodes[nodeId]?.variantContent?.[worldKey]?.variants.find(
        (v) => v.choiceId === choiceId
      );
      return variant ? escapeInk(variant.variantLine) : '';
    }
  );

  // Main text for memory recall nodes
  ink = ink.replace(
    /\{\{TEXT_MAIN\.(S3A_NPC_Recall_Positive|S3B_NPC_Recall_Negative)\}\}/g,
    (_m, nodeId) => {
      const isGoodWorld = nodeId === 'S3A_NPC_Recall_Positive';
      const before = isGoodWorld
        ? nodes[nodeId]?.textBeforeGoodWorld
        : nodes[nodeId]?.textBeforeBadWorld;
      const after = isGoodWorld
        ? nodes[nodeId]?.textAfterGoodWorld
        : nodes[nodeId]?.textAfterBadWorld;
      return escapeInk(`${before || ''}${after || ''}`);
    }
  );

  // Choices for good/bad versions handled by generic CHOICE_TEXT above

  return ink;
}

export function downloadFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
